import { type ReactNode } from 'react'
import * as ContextMenu from '@radix-ui/react-context-menu'
import { Pencil, Palette, Plus, Trash2 } from 'lucide-react'
import { FolderColorPicker } from './FolderColorPicker'

interface FolderContextMenuProps {
  children: ReactNode
  onRename: () => void
  onChangeColor: (color: string) => void
  onAddApp: (appName: string) => void
  onDelete: () => void
  currentColor: string
  availableApps: { name: string; display_name?: string | null }[]
  folderAppNames?: string[]
}

export function FolderContextMenu({
  children,
  onRename,
  onChangeColor,
  onAddApp,
  onDelete,
  currentColor,
  availableApps,
  folderAppNames = [],
}: FolderContextMenuProps) {
  const filteredApps = availableApps.filter(
    (app) => !folderAppNames.includes(app.name)
  )

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50">
          <ContextMenu.Item
            className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
            onSelect={onRename}
          >
            <Pencil className="w-4 h-4" />
            Rename
          </ContextMenu.Item>

          <ContextMenu.Sub>
            <ContextMenu.SubTrigger className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none">
              <Palette className="w-4 h-4" />
              Change Color
            </ContextMenu.SubTrigger>
            <ContextMenu.Portal>
              <ContextMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg p-1 z-50">
                <FolderColorPicker
                  currentColor={currentColor}
                  onSelect={onChangeColor}
                />
              </ContextMenu.SubContent>
            </ContextMenu.Portal>
          </ContextMenu.Sub>

          <ContextMenu.Sub>
            <ContextMenu.SubTrigger className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none">
              <Plus className="w-4 h-4" />
              Add Apps
            </ContextMenu.SubTrigger>
            <ContextMenu.Portal>
              <ContextMenu.SubContent className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] max-h-[200px] overflow-y-auto z-50">
                {filteredApps.length === 0 ? (
                  <ContextMenu.Item
                    className="px-3 py-1.5 text-sm text-muted-foreground cursor-default outline-none"
                    disabled
                  >
                    No apps
                  </ContextMenu.Item>
                ) : (
                  filteredApps.map((app) => (
                    <ContextMenu.Item
                      key={app.name}
                      className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer outline-none"
                      onSelect={() => onAddApp(app.name)}
                    >
                      {app.display_name || app.name}
                    </ContextMenu.Item>
                  ))
                )}
              </ContextMenu.SubContent>
            </ContextMenu.Portal>
          </ContextMenu.Sub>

          <ContextMenu.Separator className="h-px bg-border my-1" />

          <ContextMenu.Item
            className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none text-destructive"
            onSelect={onDelete}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}
